import { useEffect, useRef, type RefObject } from 'react';

/** Open modals (dialogs, drawers), topmost last: only the topmost answers Esc and traps Tab. */
const stack: HTMLElement[] = [];

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Focus handling of a modal: focus moves inside on open (`[data-autofocus]`, else the first field, else the first
 * button), Tab stays inside, Esc calls `onEscape`, and on close focus goes back to `returnTo()` or to what had it
 * before (a row the person clicked: Safari does not focus a clicked button, so callers can name it).
 */
export function useModalFocus(ref: RefObject<HTMLElement | null>, onEscape: () => void, returnTo?: () => HTMLElement | null) {
  const esc = useRef(onEscape);
  esc.current = onEscape;
  const back = useRef(returnTo);
  back.current = returnTo;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const box = ref.current!;
    stack.push(box);
    (box.querySelector<HTMLElement>('[data-autofocus], input, select, textarea') ?? box.querySelector<HTMLElement>('button'))?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== box) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        esc.current();
      }
      if (e.key !== 'Tab') return;
      const f = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      stack.splice(stack.indexOf(box), 1);
      (back.current?.() ?? prev)?.focus?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
